/**
 * DigitalOfficeSection Component
 * Shows independent voice rooms (decoupled from channels)
 */

import React, { useState } from 'react';
import { Building2 } from 'lucide-react';
import { useVoiceRooms } from '@/hooks/useVoiceRooms';
import { useVoiceSessions } from '@/hooks/useVoiceSessions';
import { useLiveKit } from '@/contexts/LiveKitContext';
import { VoiceRoomCard } from './VoiceRoomCard';
import { CreateVoiceRoomModal } from './CreateVoiceRoomModal';
import { useAuthStore } from '@/store/auth.store';

interface DigitalOfficeSectionProps {
  children: (props: {
    title: string;
    icon: React.ReactNode;
    badge: number;
    content: React.ReactNode;
    onAdd?: () => void;
  }) => React.ReactNode;
}

export const DigitalOfficeSection: React.FC<DigitalOfficeSectionProps> = ({ children }) => {
  const { data: voiceRooms } = useVoiceRooms();
  const { data: sessionsData } = useVoiceSessions();
  const { state: liveKitState } = useLiveKit();
  const { user } = useAuthStore();
  const [showCreateModal, setShowCreateModal] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const voiceRoomSessions = sessionsData?.voiceRoomSessions || [];

  // Calculate total online count from voice room sessions
  const totalOnline = voiceRoomSessions.reduce(
    (sum, session) => sum + session.participants.length,
    0
  );

  const content = (
    <div className="space-y-1 px-2">
      {voiceRooms
        ?.filter((room) => !room.isArchived)
        .map((room) => {
          const session = voiceRoomSessions.find(
            (s) => s.voiceRoomId === room.id
          );
          const isUserConnected =
            liveKitState.isConnected && liveKitState.voiceRoomId === room.id;

          return (
            <VoiceRoomCard
              key={`voice-room-${room.id}`}
              voiceRoom={room}
              voiceRoomSession={session || null}
              isUserConnected={isUserConnected}
            />
          );
        })}

      {(!voiceRooms || voiceRooms.length === 0) && (
        <div className="text-xs text-gray-500 py-2 px-2 text-center">
          Няма стаи
          {!isAdmin && (
            <span className="block mt-1 text-gray-500/80">
              Стаите се създават от администратор. Ще можете да се присъедините веднага щом се появи стая.
            </span>
          )}
        </div>
      )}

      {showCreateModal && (
        <CreateVoiceRoomModal onClose={() => setShowCreateModal(false)} />
      )}
    </div>
  );

  return (
    <>
      {children({
        title: 'Дигитален офис',
        icon: <Building2 className="w-4 h-4" />,
        badge: totalOnline,
        content,
        onAdd: isAdmin ? () => setShowCreateModal(true) : undefined,
      })}
    </>
  );
};
